import { useMemo, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { adapter } from "@/services/db";
import { useAuth } from "@/context/AuthContext";
import { useCollection } from "@/hooks/useCollection";
import api from "@/lib/api";
import { cn } from "@/lib/utils";

// Professional categories (trades: Plumber, Electrician, Tile mason…) and which services each one does.
//   professional_categories/{id}  name, description, order, legacy_category_ids
//   categories/{id}.pro_category_ids   the professional categories that do this service (many allowed)
//   professionals/{id}.pro_category_ids the pro's trades (Professionals → profile)
// Command Centre's backend keeps every pro's category_ids in step (backend/pro_links.py), so the
// existing lead float sends a request to every linked trade. brugo.in reads the links too.
const now = () => new Date().toISOString();
export const slugOf = (s) => String(s || "").toLowerCase().trim().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || `trade-${Date.now().toString(36)}`;
const errMsg = (e) => (e?.code === "permission-denied" ? "Permission denied — publish the professional_categories rule (ops or admin role)" : e?.message);
const log = (user, id, action, description) => adapter.add("activity_logs", { app: "ops", entity_type: "professional_categories", entity_id: id, action, change_type: action, description, performed_by: user?.email || null, performed_by_role: user?.role || null, status: "applied", created_at: now(), server_ts: now() }).catch(() => {});

// Trades a pro does: set on the profile, else worked out from their main category.
export function tradesOfPro(p = {}, procats = []) {
  if (Array.isArray(p.pro_category_ids) && p.pro_category_ids.length) return p.pro_category_ids.map(String);
  const own = [p.category_id, p.category].filter(Boolean).map(String);
  return procats.filter((t) => (t.legacy_category_ids || []).some((c) => own.includes(String(c)))).map((t) => t.id);
}

// Multi-pick chips used by the service editor and the professional profile.
export function TradePicker({ value = [], onChange, procats, testId = "trade-picker", emptyHint }) {
  const sorted = [...procats].sort((a, b) => (a.order ?? 999) - (b.order ?? 999) || String(a.name).localeCompare(String(b.name)));
  if (!sorted.length) return <p className="text-xs text-muted-foreground">{emptyHint || "No professional categories yet — add them in Catalog → Professional categories."}</p>;
  const set = new Set(value.map(String));
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {sorted.map((t) => {
        const on = set.has(t.id);
        return (
          <button key={t.id} type="button" onClick={() => onChange(on ? value.filter((x) => String(x) !== t.id) : [...value, t.id])} data-testid={`${testId}-${t.id}`}
            className={cn("rounded-full border px-2.5 py-1 text-xs transition-colors", on ? "border-primary bg-primary/10 text-primary font-medium" : "border-border text-muted-foreground hover:border-primary/40")}>
            {on && <Icons.Check className="inline h-3 w-3 mr-1" />}{t.name}
          </button>
        );
      })}
    </div>
  );
}

function AddDialog({ open, onClose, existing }) {
  const { user } = useAuth();
  const [txt, setTxt] = useState("");
  const [busy, setBusy] = useState(false);
  const names = [...new Set(txt.split(/[\n,]/).map((x) => x.trim()).filter(Boolean))];
  const have = new Set(existing.map((c) => String(c.name).toLowerCase()));
  const fresh = names.filter((n) => !have.has(n.toLowerCase()));
  const add = async () => {
    setBusy(true);
    try {
      let order = existing.length;
      for (const name of fresh) {
        let id = slugOf(name);
        while (existing.some((c) => c.id === id)) id = `${id}-2`;
        await adapter.set("professional_categories", id, { name, order: order++, description: "", legacy_category_ids: [], created_at: now(), updated_at: now(), created_by: user?.email || null });
      }
      await log(user, "bulk", "professional_categories_added", fresh.join(", "));
      toast.success(`${fresh.length} professional categor${fresh.length === 1 ? "y" : "ies"} added`); setTxt(""); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="procat-add">
        <DialogHeader><DialogTitle className="font-heading">Add professional categories</DialogTitle>
          <DialogDescription>One per line. Then link each service to the categories that do it.</DialogDescription></DialogHeader>
        <Textarea rows={8} value={txt} onChange={(e) => setTxt(e.target.value)} placeholder={"Plumber\nElectrician\nTile mason\nCarpenter\nPainter\nPOP / false ceiling"} data-testid="procat-add-text" />
        <p className="text-xs text-muted-foreground">{fresh.length} new{names.length - fresh.length ? ` · ${names.length - fresh.length} already exist` : ""}</p>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={add} disabled={busy || !fresh.length} data-testid="procat-add-save">{busy ? "Adding…" : `Add ${fresh.length}`}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({ t, onClose, categories, procats }) {
  const { user } = useAuth();
  const [name, setName] = useState(t.name || "");
  const [desc, setDesc] = useState(t.description || "");
  const [legacy, setLegacy] = useState((t.legacy_category_ids || []).map(String));
  const [services, setServices] = useState(categories.filter((c) => (c.pro_category_ids || []).map(String).includes(t.id)).map((c) => c.id));
  const [busy, setBusy] = useState(false);
  const toggle = (arr, set, id) => set(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
  const save = async () => {
    const nm = name.trim();
    if (!nm) return toast.error("Name can't be empty");
    if (procats.some((x) => x.id !== t.id && String(x.name).toLowerCase() === nm.toLowerCase())) return toast.error("Another professional category has that name");
    setBusy(true);
    try {
      await adapter.update("professional_categories", t.id, { name: nm, description: desc, legacy_category_ids: legacy, updated_at: now() });
      // Services: add / remove this trade from each service's pro_category_ids.
      const before = new Set(categories.filter((c) => (c.pro_category_ids || []).map(String).includes(t.id)).map((c) => c.id));
      const after = new Set(services);
      const changes = categories.filter((c) => before.has(c.id) !== after.has(c.id));
      for (const c of changes) {
        const cur = (c.pro_category_ids || []).map(String);
        await adapter.update("categories", c.id, { pro_category_ids: after.has(c.id) ? [...new Set([...cur, t.id])] : cur.filter((x) => x !== t.id), updated_at: now() });
      }
      await log(user, t.id, "professional_category_saved", `${nm}: does ${services.length} service(s)`);
      toast.success("Saved — lead matching updates in a few seconds"); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const sortedCats = [...categories].sort((a, b) => String(a.name).localeCompare(String(b.name)));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="procat-edit">
        <DialogHeader><DialogTitle className="font-heading">Edit {t.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1"><p className="text-xs text-muted-foreground">Name</p><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="procat-edit-name" /></div>
          <div className="space-y-1"><p className="text-xs text-muted-foreground">Short description (optional)</p><Input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="e.g. Pipes, taps, bathroom fittings" /></div>
          <div className="space-y-1.5">
            <p className="text-sm font-medium">Services this professional category does</p>
            <p className="text-[11px] text-muted-foreground">A request for any of these services is floated to pros in this category.</p>
            <div className="flex flex-wrap gap-1.5" data-testid="procat-services">
              {sortedCats.map((c) => <Chip key={c.id} on={services.includes(c.id)} onClick={() => toggle(services, setServices, c.id)} testId={`procat-svc-${c.id}`}>{c.name}</Chip>)}
            </div>
          </div>
          <div className="space-y-1.5">
            <p className="text-sm font-medium">Pros already in Brugo with this main category</p>
            <p className="text-[11px] text-muted-foreground">Pros signed up under one of these get this professional category automatically (unless their profile sets its own).</p>
            <div className="flex flex-wrap gap-1.5" data-testid="procat-legacy">
              {sortedCats.map((c) => <Chip key={c.id} on={legacy.includes(c.id)} onClick={() => toggle(legacy, setLegacy, c.id)} testId={`procat-legacy-${c.id}`}>{c.name}</Chip>)}
            </div>
          </div>
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={busy} data-testid="procat-edit-save">{busy ? "Saving…" : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Chip({ on, onClick, children, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={cn("rounded-full border px-2.5 py-1 text-xs transition-colors", on ? "border-primary bg-primary/10 text-primary font-medium" : "border-border text-muted-foreground hover:border-primary/40")}>
      {on && <Icons.Check className="inline h-3 w-3 mr-1" />}{children}
    </button>
  );
}

export function ProCategoriesTab({ procats, categories }) {
  const { user } = useAuth();
  const pros = useCollection("professionals");
  const [adding, setAdding] = useState(false);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState("");
  const sorted = useMemo(() => [...procats].sort((a, b) => (a.order ?? 999) - (b.order ?? 999) || String(a.name).localeCompare(String(b.name))), [procats]);
  const servicesOf = (tid) => categories.filter((c) => (c.pro_category_ids || []).map(String).includes(tid));
  const prosOf = useMemo(() => {
    const m = {};
    pros.forEach((p) => tradesOfPro(p, procats).forEach((t) => { m[t] = (m[t] || 0) + 1; }));
    return m;
  }, [pros, procats]);
  const unlinked = categories.filter((c) => !(c.pro_category_ids || []).length && c.status !== "inactive");

  // One professional category per existing service, each service linked to its own — exactly
  // today's matching, ready to extend (e.g. add Tile mason to Bathroom renovation).
  const seed = async () => {
    setBusy("seed");
    try {
      let i = sorted.length;
      for (const c of categories) {
        if ((c.pro_category_ids || []).length) continue;
        // A professional category that already covers this service's pros is reused.
        const covering = procats.find((t) => (t.legacy_category_ids || []).map(String).includes(c.id));
        let id = covering ? covering.id : slugOf(c.name);
        const ex = procats.find((t) => t.id === id);
        if (!ex) await adapter.set("professional_categories", id, { name: c.name, order: i++, description: "", legacy_category_ids: [c.id], created_at: now(), updated_at: now(), created_by: user?.email || null });
        else if (!(ex.legacy_category_ids || []).includes(c.id)) await adapter.update("professional_categories", id, { legacy_category_ids: [...(ex.legacy_category_ids || []), c.id], updated_at: now() });
        await adapter.update("categories", c.id, { pro_category_ids: [id], updated_at: now() });
      }
      await log(user, "seed", "professional_categories_seeded", "one per service");
      toast.success("Created from your services — now add extra links where a service needs more than one trade");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const syncNow = async () => {
    setBusy("sync");
    try { const r = (await api.post("/cc/pro-links/sync")).data; toast.success(`Lead matching updated · ${r.changed} professional${r.changed === 1 ? "" : "s"} changed`); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); } finally { setBusy(""); }
  };
  const remove = async (t) => {
    if (servicesOf(t.id).length) return toast.error("Unlink it from its services first (Edit)");
    try { await adapter.remove("professional_categories", t.id); await log(user, t.id, "professional_category_deleted", t.name); toast.success("Deleted"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Card data-testid="procat-tab">
      <div className="p-4 border-b border-border flex flex-wrap items-center gap-2">
        <div className="max-w-2xl"><h3 className="font-heading font-semibold text-sm flex items-center gap-1.5 mb-1"><Icons.HardHat className="h-4 w-4 text-primary" />Professional categories ({procats.length})</h3><p className="text-xs text-muted-foreground">Professional categories are the trades pros work in. Link every service to one or more of them (e.g. <b>Bathroom renovation → Plumber + Tile mason</b>) and a request floats to all of those pros. Set a pro's categories on their profile in Professionals. brugo.in shows the customer which pros are being notified. A service with no professional category is left off brugo.in.</p></div>
        <div className="ml-auto flex gap-2">
          {unlinked.length > 0 && <Button size="sm" variant="outline" onClick={seed} disabled={!!busy} data-testid="procat-seed"><Icons.Wand2 className="h-4 w-4 mr-1" />{busy === "seed" ? "Creating…" : `Create from ${unlinked.length} unlinked service${unlinked.length === 1 ? "" : "s"}`}</Button>}
          <Button size="sm" variant="outline" onClick={syncNow} disabled={!!busy} data-testid="procat-sync"><Icons.RefreshCw className={cn("h-4 w-4 mr-1", busy === "sync" && "animate-spin")} />Update lead matching now</Button>
          <Button size="sm" onClick={() => setAdding(true)} data-testid="procat-add-open"><Icons.Plus className="h-4 w-4 mr-1" />Add</Button>
        </div>
      </div>
      {unlinked.length > 0 && procats.length > 0 && (
        <p className="px-4 py-2 text-xs bg-amber-50/70 text-amber-800 border-b border-amber-100" data-testid="procat-unlinked">
          <Icons.AlertTriangle className="inline h-3.5 w-3.5 mr-1" />Not linked yet: {unlinked.map((c) => c.name).join(", ")} — {categories.some((c) => (c.pro_category_ids || []).length) ? "these are hidden on brugo.in until linked." : "brugo.in keeps showing every service until you link the first one."}
        </p>
      )}
      <Table>
        <TableHeader><TableRow><TableHead>Professional category</TableHead><TableHead>Does these services</TableHead><TableHead>Pros</TableHead><TableHead className="text-right">Edit</TableHead></TableRow></TableHeader>
        <TableBody>
          {sorted.map((t) => (
            <TableRow key={t.id} data-testid={`procat-row-${t.id}`}>
              <TableCell><p className="font-medium">{t.name}</p>{t.description && <p className="text-[11px] text-muted-foreground">{t.description}</p>}</TableCell>
              <TableCell className="text-xs max-w-[420px]">{servicesOf(t.id).map((c) => <span key={c.id} className="inline-block mr-1 mb-1 rounded-full bg-secondary px-2 py-0.5">{c.name}</span>)}{!servicesOf(t.id).length && <span className="text-amber-700">No services linked</span>}</TableCell>
              <TableCell className="font-mono text-sm">{prosOf[t.id] || 0}</TableCell>
              <TableCell className="text-right whitespace-nowrap">
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEdit(t)} data-testid={`procat-edit-${t.id}`}><Icons.Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => remove(t)} data-testid={`procat-del-${t.id}`}><Icons.Trash2 className="h-4 w-4" /></Button>
              </TableCell>
            </TableRow>
          ))}
          {!procats.length && <TableRow><TableCell colSpan={4} className="text-center py-10 text-muted-foreground">No professional categories yet. Create them from your services in one click, then add the extra links.</TableCell></TableRow>}
        </TableBody>
      </Table>
      <AddDialog open={adding} onClose={() => setAdding(false)} existing={procats} />
      {edit && <EditDialog key={edit.id} t={edit} onClose={() => setEdit(null)} categories={categories} procats={procats} />}
    </Card>
  );
}
